import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { injectJsonLd, removeJsonLd, buildBreadcrumbSchema } from '../../lib/schemaBuilder';
import {
  buildSpeakableSchema,
  buildEnhancedFAQSchema,
  DEFAULT_SPEAKABLE_SELECTORS,
  DEFAULT_AUTHOR,
} from '../../lib/aeoSchemaBuilder';
import { pageFaqs } from '../../mocks/pageFaqs';
import { CORE_BUSINESS, SCHEMA_IDS } from '../../lib/coreSchemas';

const BASE_URL = import.meta.env.VITE_SITE_URL || 'https://rockhoundauto.com';

const SCHEMA_IDS_LOCAL = [
  'schema-manager-webpage',
  'schema-manager-speakable',
  'schema-manager-faq',
  'schema-manager-itemlist',
  'schema-manager-howto',
  'schema-manager-page-faq',
  'schema-manager-service',
  'schema-manager-breadcrumb',
  'schema-manager-localbusiness',
  'schema-manager-article',
  'schema-manager-video',
  'schema-manager-event',
];

function cleanup() {
  SCHEMA_IDS_LOCAL.forEach(removeJsonLd);
}

function buildLocalBusiness(overrides?: Record<string, unknown>) {
  return {
    '@context': 'https://schema.org',
    '@type': ['AutomotiveBusiness', 'LocalBusiness'],
    '@id': SCHEMA_IDS.localBusiness,
    name: CORE_BUSINESS.name,
    url: CORE_BUSINESS.url,
    telephone: CORE_BUSINESS.telephone,
    email: CORE_BUSINESS.email,
    address: {
      '@type': 'PostalAddress',
      ...CORE_BUSINESS.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: CORE_BUSINESS.geo.latitude,
      longitude: CORE_BUSINESS.geo.longitude,
    },
    openingHoursSpecification: CORE_BUSINESS.openingHours,
    priceRange: CORE_BUSINESS.priceRange,
    areaServed: [
      { '@type': 'State', name: 'Oregon' },
      { '@type': 'State', name: 'Washington' },
    ],
    sameAs: CORE_BUSINESS.sameAs,
    ...overrides,
  };
}

function injectForPath(pathname: string) {
  cleanup();

  // ── / (homepage) ──────────────────────────────────────────────────────────
  if (pathname === '/') {
    injectJsonLd('schema-manager-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Where is Rockhound Auto located?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Rockhound Auto is located at 1425 SE 9th St, Warrenton, OR 97146 — just 10 minutes from Astoria on the Oregon Coast. We serve buyers across the entire Oregon Coast and SW Washington, and offer free delivery within 50 miles.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is a flat doc fee and how much does Rockhound Auto charge?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A doc fee (documentation fee) covers the paperwork involved in a vehicle sale. Most Oregon dealerships charge $500–$1,200. Rockhound Auto charges a flat $250 doc fee — no add-ons, no negotiating, no surprises.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I buy a car from Rockhound Auto if I have bad credit?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. We work with lenders across Oregon and Washington who specialize in all credit situations — including bad credit, no credit, and first-time buyers. We shop your loan to multiple lenders to find the best available rate for your situation.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is an auto broker and how does Rockhound Auto\'s broker service work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'An auto broker finds, inspects, and negotiates the purchase of a vehicle on your behalf. You tell us the make, model, year, and budget you want. We search our nationwide dealer network, inspect the vehicle, negotiate the price, and deliver it to your door — for a flat broker fee. Most clients save far more than the fee.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does Rockhound Auto deliver vehicles to my home?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. We offer free delivery within 50 miles of Warrenton, Oregon. This covers most of the Oregon Coast including Astoria, Seaside, Cannon Beach, and Tillamook, as well as parts of SW Washington. Delivery beyond 50 miles can be arranged for a fee.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does Oregon have sales tax on vehicle purchases?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Oregon has no state sales tax, which means Washington state buyers who purchase a vehicle from Rockhound Auto in Warrenton, OR can save thousands compared to buying in Washington. You pay Oregon\'s flat $250 doc fee instead of Washington\'s sales tax.',
          },
        },
        // ── Buying process step FAQs ──────────────────────────────────────
        {
          '@type': 'Question',
          name: 'How do I browse Rockhound Auto\'s inventory without pressure?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Browse our full inventory online at rockhoundauto.com at your own pace. Every listing includes real photos, transparent pricing, full specs, and a free CarFax report — no "call for price," no mystery packages, and no salesperson following you around. You can filter by make, model, price, and mileage to find exactly what you need.',
          },
        },
        {
          '@type': 'Question',
          name: 'What happens when I contact Rockhound Auto about a vehicle?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You\'ll speak directly with a real person — not a call center, not a bot, and not a "closer" from the back office. We answer questions honestly, provide straight pricing, and never use high-pressure scripts. You can call, text, or email and expect a straightforward conversation.',
          },
        },
        {
          '@type': 'Question',
          name: 'How fast can I get financing approved at Rockhound Auto?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Same-day financing approval is available in most cases. We work with multiple lenders who specialize in all credit situations — including bad credit, no credit history, and first-time buyers. Apply online in minutes and we\'ll shop your application to find the best available rate.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does the car buying process take at Rockhound Auto?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most customers complete the entire process — from browsing to driving home — in under 2 hours. There are no marathon negotiations, no back-office theater, and no waiting around. We handle the paperwork so you can focus on the good feeling of driving your new car home.',
          },
        },
      ],
    });

    // ── HowTo schema for the buying process steps ─────────────────────────
    injectJsonLd('schema-manager-howto', {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      '@id': `${BASE_URL}/#buying-process`,
      name: 'How to Buy a Used Car at Rockhound Auto — 4 Simple Steps',
      description: 'The Rockhound Auto buying process: browse real inventory, talk to a real human, get approved fast, and drive home happy. Most customers are done in under 2 hours.',
      image: {
        '@type': 'ImageObject',
        url: CORE_BUSINESS.logo,
      },
      totalTime: 'PT2H',
      estimatedCost: {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: '250',
        description: 'Flat $250 doc fee — no hidden add-ons.',
      },
      supply: [
        { '@type': 'HowToSupply', name: 'Valid driver\'s license' },
        { '@type': 'HowToSupply', name: 'Proof of insurance' },
        { '@type': 'HowToSupply', name: 'Down payment (if financing)' },
      ],
      tool: [
        { '@type': 'HowToTool', name: 'Rockhound Auto online inventory' },
        { '@type': 'HowToTool', name: 'Online financing pre-approval form' },
      ],
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Browse Without the BS',
          text: 'Browse our full inventory online. Every listing has real photos, transparent pricing, full specs, and a free CarFax report. No "call for price." No mystery packages. Just actual numbers you can act on.',
          url: `${BASE_URL}/inventory`,
          image: { '@type': 'ImageObject', url: CORE_BUSINESS.logo },
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Talk to a Real Human',
          text: 'Ask anything — straight answers, no scripts, no pressure, no closer from the back office. We consult. You decide. Reach us by call, text, or email.',
          url: `${BASE_URL}/contact`,
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Get Approved Fast',
          text: 'Same-day financing available. Bad credit welcome. We work with lenders who deal in reality, not just ideal scenarios. Apply online in minutes and get a real rate.',
          url: `${BASE_URL}/apply-for-financing`,
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Drive Home Happy',
          text: 'We handle the paperwork. You handle the good feeling. Know exactly what you paid — and that a dog in Oregon is rooting for you. Free delivery within 50 miles.',
          url: `${BASE_URL}/how-it-works`,
        },
      ],
    });

    return;
  }

  // ── /best-used-cars-oregon-coast ──────────────────────────────────────────
  if (pathname === '/best-used-cars-oregon-coast') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/best-used-cars-oregon-coast#webpage`,
      name: 'Best Used Cars for the Oregon Coast | Rockhound Auto Broker',
      description: 'Discover the best used cars for Oregon Coast driving conditions. Expert guides for SUVs, trucks, commuter cars, and AWD vehicles under $20k.',
      url: `${BASE_URL}/best-used-cars-oregon-coast`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      about: { '@id': SCHEMA_IDS.localBusiness },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Best Used Cars Oregon Coast', item: `${BASE_URL}/best-used-cars-oregon-coast` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      keywords: 'best used cars oregon coast, used cars Warrenton, AWD vehicles Oregon, used SUVs coastal weather',
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-speakable', buildSpeakableSchema(pathname, DEFAULT_SPEAKABLE_SELECTORS));
    injectJsonLd('schema-manager-itemlist', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Best Used Cars for Oregon Coast Driving',
      description: 'Curated vehicle categories for Oregon Coast weather and terrain',
      numberOfItems: 6,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Best Used SUVs for Coastal Weather', url: `${BASE_URL}/best-used-suvs-coastal-weather` },
        { '@type': 'ListItem', position: 2, name: 'Best Trucks for Outdoor Driving', url: `${BASE_URL}/best-trucks-under-30k-oregon` },
        { '@type': 'ListItem', position: 3, name: 'Best Cars for Commuting Portland to Coast', url: `${BASE_URL}/best-cars-commuting-portland` },
        { '@type': 'ListItem', position: 4, name: 'Best AWD Vehicles for Oregon Coast', url: `${BASE_URL}/awd-vehicles-oregon-coast` },
        { '@type': 'ListItem', position: 5, name: 'Best Used Cars Under $20,000', url: `${BASE_URL}/used-cars-under-20000` },
        { '@type': 'ListItem', position: 6, name: 'Best Family SUVs for Oregon', url: `${BASE_URL}/family-suvs-oregon` },
      ],
    });
    return;
  }

  // ── /car-buying-guide ─────────────────────────────────────────────────────
  if (pathname === '/car-buying-guide' || pathname.startsWith('/car-buying-guide/')) {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}${pathname}#webpage`,
      name: 'Car Buying Guide | Rockhound Auto',
      description: 'Expert car buying guides for Oregon and Washington shoppers.',
      url: `${BASE_URL}${pathname}`,
      speakable: { '@type': 'SpeakableSpecification', cssSelector: ['h1', 'h2', '.speakable-headline', '.intro-summary'] },
      inLanguage: 'en-US',
    });
    injectJsonLd('schema-manager-speakable', buildSpeakableSchema(pathname, ['h1', 'h2', '.speakable-headline', '.intro-summary']));
    return;
  }

  // ── /why-use-a-broker ─────────────────────────────────────────────────────
  if (pathname === '/why-use-a-broker') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/why-use-a-broker#webpage`,
      name: 'Why Use an Auto Broker in Oregon? | Rockhound Auto',
      description: 'Learn why using an auto broker saves you time and money vs. traditional dealerships. Rockhound Auto serves the Oregon Coast and SW Washington.',
      url: `${BASE_URL}/why-use-a-broker`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Why Use a Broker', item: `${BASE_URL}/why-use-a-broker` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Auto Broker Services — Oregon Coast & SW Washington',
      provider: { '@id': SCHEMA_IDS.organization },
      serviceType: 'Auto Broker',
      areaServed: [{ '@type': 'State', name: 'Oregon' }, { '@type': 'State', name: 'Washington' }],
      description: 'Professional auto broker services. We source, negotiate, and deliver any vehicle — saving you thousands vs. traditional dealerships.',
      url: `${BASE_URL}/why-use-a-broker`,
    });
    return;
  }

  // ── /broker-services/why-broker ───────────────────────────────────────────
  if (pathname === '/broker-services/why-broker') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/broker-services/why-broker#webpage`,
      name: 'Why Use a Car Broker? | Rockhound Auto Broker Services',
      description: 'Discover why smart Oregon and Washington car buyers use an auto broker instead of a traditional dealership. Save time, money, and stress.',
      url: `${BASE_URL}/broker-services/why-broker`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Broker Services', item: `${BASE_URL}/broker-services` },
        { name: 'Why Use a Broker', item: `${BASE_URL}/broker-services/why-broker` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /broker-services ─────────────────────────────────────────────────────
  if (pathname === '/broker-services') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/broker-services#webpage`,
      name: 'Auto Broker Services Oregon Coast | Rockhound Auto',
      description: 'Professional auto broker services in Warrenton, Oregon. We source any vehicle, negotiate the best price, and deliver it to you. Serving OR and WA.',
      url: `${BASE_URL}/broker-services`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Broker Services', item: `${BASE_URL}/broker-services` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Auto Broker Services',
      provider: { '@id': SCHEMA_IDS.organization },
      serviceType: 'Auto Broker',
      areaServed: [{ '@type': 'State', name: 'Oregon' }, { '@type': 'State', name: 'Washington' }],
      description: 'We source, negotiate, and deliver any vehicle from any dealership nationwide. No pressure, no games — just the car you want at the best price.',
      url: `${BASE_URL}/broker-services`,
    });
    return;
  }

  // ── /broker-services/request-a-vehicle ───────────────────────────────────
  if (pathname === '/broker-services/request-a-vehicle') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/broker-services/request-a-vehicle#webpage`,
      name: 'Request a Vehicle | Rockhound Auto Broker',
      description: 'Tell us what vehicle you want and we\'ll find it. Rockhound Auto sources any make, model, or year from our nationwide dealer network.',
      url: `${BASE_URL}/broker-services/request-a-vehicle`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Broker Services', item: `${BASE_URL}/broker-services` },
        { name: 'Request a Vehicle', item: `${BASE_URL}/broker-services/request-a-vehicle` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'How does the vehicle request process work?', acceptedAnswer: { '@type': 'Answer', text: 'Submit your vehicle request with details about make, model, year, features, and budget. We search our dealer network and auctions nationwide to find matching vehicles. Within 7-14 days, we present options with full details, pricing, and history reports. You review and decide—no obligation to purchase.' } },
        { '@type': 'Question', name: 'How much does it cost to request a vehicle?', acceptedAnswer: { '@type': 'Answer', text: 'There\'s no upfront cost to submit a vehicle request. Our broker fee of $500-$1,500 is only charged if you purchase a vehicle we source for you. The fee is disclosed upfront and often offset by the savings we negotiate on your behalf through wholesale access.' } },
        { '@type': 'Question', name: 'Can you find any make or model?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, we can source virtually any make, model, year, and configuration through our nationwide dealer network and auction access. Whether you want a specific Toyota Tacoma trim or a rare luxury vehicle, we have the resources to locate it. Exotic and vintage vehicles may require additional search time.' } },
      ],
    });
    return;
  }

  // ── /broker-services/:city pages ──────────────────────────────────────────
  const brokerCityPages: Record<string, { city: string; state: string }> = {
    '/broker-services/portland': { city: 'Portland', state: 'Oregon' },
    '/broker-services/seattle': { city: 'Seattle', state: 'Washington' },
    '/broker-services/bend': { city: 'Bend', state: 'Oregon' },
    '/broker-services/eugene': { city: 'Eugene', state: 'Oregon' },
    '/broker-services/medford': { city: 'Medford', state: 'Oregon' },
  };
  if (brokerCityPages[pathname]) {
    const { city, state } = brokerCityPages[pathname];
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}${pathname}#webpage`,
      name: `Auto Broker ${city} ${state} | Rockhound Auto`,
      description: `Auto broker services in ${city}, ${state}. Rockhound Auto sources any vehicle, negotiates the best price, and delivers to ${city}. No dealership hassle.`,
      url: `${BASE_URL}${pathname}`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Broker Services', item: `${BASE_URL}/broker-services` },
        { name: city, item: `${BASE_URL}${pathname}` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: `Auto Broker Services — ${city}, ${state}`,
      provider: { '@id': SCHEMA_IDS.organization },
      serviceType: 'Auto Broker',
      areaServed: [{ '@type': 'City', name: city, containedInPlace: { '@type': 'State', name: state } }],
      description: `Professional auto broker serving ${city}, ${state}. We source, negotiate, and deliver any vehicle — saving you time and money.`,
      url: `${BASE_URL}${pathname}`,
    });
    return;
  }

  // ── /first-time-buyer-guide ───────────────────────────────────────────────
  if (pathname === '/first-time-buyer-guide') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/first-time-buyer-guide#webpage`,
      name: 'First-Time Car Buyer Guide Oregon | Rockhound Auto',
      description: 'Complete guide for first-time car buyers in Oregon. Learn about financing, credit scores, what to inspect, and how to avoid common mistakes.',
      url: `${BASE_URL}/first-time-buyer-guide`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'First-Time Buyer Guide', item: `${BASE_URL}/first-time-buyer-guide` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'What credit score do I need to buy my first car in Oregon?', acceptedAnswer: { '@type': 'Answer', text: 'Most first-time buyers in Oregon can get approved with a credit score of 580 or higher. Some lenders offer first-time buyer programs with no credit history required. A steady income and a down payment of 10–20% significantly improve your chances.' } },
        { '@type': 'Question', name: 'How much should a first-time car buyer put down in Oregon?', acceptedAnswer: { '@type': 'Answer', text: 'A down payment of 10–20% of the vehicle price is recommended for first-time buyers. This lowers your monthly payment, reduces interest paid over the loan term, and helps you avoid being upside-down on your loan.' } },
        { '@type': 'Question', name: 'What should I look for when buying my first used car?', acceptedAnswer: { '@type': 'Answer', text: 'Check the vehicle history report (Carfax or AutoCheck), have a pre-purchase inspection done by an independent mechanic, look for rust especially on the undercarriage, test all electronics, and verify the title is clean. On the Oregon Coast, pay special attention to rust from salt air exposure.' } },
      ],
    });
    return;
  }

  // ── /financing ────────────────────────────────────────────────────────────
  if (pathname === '/financing') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/financing#webpage`,
      name: 'Car Financing Oregon Coast | Credit Union Approved Dealer | Rockhound Auto',
      description: 'Transparent car financing in Warrenton, OR. Credit union approved dealer. No hidden fees, no dealer markup. All credit types welcome. Get pre-approved today.',
      url: `${BASE_URL}/financing`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: { '@id': `${BASE_URL}/financing#breadcrumb` },
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-service', {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      '@id': `${BASE_URL}/financing#financialservice`,
      name: 'Rockhound Auto Financing',
      description: 'Auto loan financing for all credit situations — bad credit, no credit, first-time buyers welcome. We shop your loan to multiple lenders to find the best rate.',
      url: `${BASE_URL}/financing`,
      telephone: CORE_BUSINESS.telephone,
      provider: { '@id': SCHEMA_IDS.organization },
      areaServed: [
        { '@type': 'State', name: 'Oregon' },
        { '@type': 'State', name: 'Washington' },
      ],
      serviceType: 'Auto Loan Financing',
    });
    injectJsonLd('schema-manager-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${BASE_URL}/financing#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Financing', item: `${BASE_URL}/financing` },
      ],
    });
    // FAQ schema is injected directly in financing/page.tsx via useEffect
    return;
  }

  // ── /sell-your-car ────────────────────────────────────────────────────────
  if (pathname === '/sell-your-car') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/sell-your-car#webpage`,
      name: 'Sell Your Car Oregon Coast | Fair Cash Offers | Rockhound Auto',
      description: 'Sell your car in Warrenton, OR without the dealer lowball. Fair cash offers in 24 hours. No pressure, no games. Serving Astoria, Seaside, and the Oregon Coast.',
      url: `${BASE_URL}/sell-your-car`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: { '@id': `${BASE_URL}/sell-your-car#breadcrumb` },
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Sell Your Car — Rockhound Auto',
      description: 'Fair cash offers for your vehicle in 24 hours. No dealer lowball, no pressure. We pay market value based on real data.',
      url: `${BASE_URL}/sell-your-car`,
      provider: { '@id': SCHEMA_IDS.organization },
      serviceType: 'Vehicle Purchase',
      areaServed: [
        { '@type': 'State', name: 'Oregon' },
        { '@type': 'State', name: 'Washington' },
      ],
    });
    injectJsonLd('schema-manager-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${BASE_URL}/sell-your-car#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Sell Your Car', item: `${BASE_URL}/sell-your-car` },
      ],
    });
    const sellCarFaqs = pageFaqs.sellYourCar;
    if (sellCarFaqs && sellCarFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(sellCarFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /consignment ──────────────────────────────────────────────────────────
  if (pathname === '/consignment') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/consignment#webpage`,
      name: 'Car Consignment Oregon Coast | Sell Your Car for More | Rockhound Auto',
      description: 'Consign your vehicle with Rockhound Auto in Warrenton, OR. We handle marketing, showings, and paperwork — you get more than a trade-in without the private sale hassle.',
      url: `${BASE_URL}/consignment`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Consignment', item: `${BASE_URL}/consignment` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Vehicle Consignment Service',
      provider: { '@id': SCHEMA_IDS.organization },
      serviceType: 'Car Consignment',
      areaServed: [{ '@type': 'State', name: 'Oregon' }, { '@type': 'State', name: 'Washington' }],
      description: 'Professional vehicle consignment. We market, show, and sell your car for you — getting you more than a trade-in without the private sale hassle.',
      url: `${BASE_URL}/consignment`,
    });
    const consignmentFaqs = pageFaqs.consignment;
    if (consignmentFaqs && consignmentFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(consignmentFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /community-impact ─────────────────────────────────────────────────────
  if (pathname === '/community-impact') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/community-impact#webpage`,
      name: 'Community Impact | Rockhound Auto & Oregon Dog Rescue',
      description: 'Rockhound Auto donates a portion of every sale to Oregon Dog Rescue. Learn about our Puplandia partnership and community commitment on the Oregon Coast.',
      url: `${BASE_URL}/community-impact`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Community Impact', item: `${BASE_URL}/community-impact` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-article', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'How Rockhound Auto Gives Back to the Oregon Coast Community',
      description: 'Every car sold at Rockhound Auto supports Oregon Dog Rescue through our Puplandia partnership. Learn how buying a car can help save a dog\'s life.',
      author: { '@id': SCHEMA_IDS.organization },
      publisher: { '@id': SCHEMA_IDS.organization },
      url: `${BASE_URL}/community-impact`,
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    const communityImpactFaqs = pageFaqs.communityImpact;
    if (communityImpactFaqs && communityImpactFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(communityImpactFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /compare ──────────────────────────────────────────────────────────────
  if (pathname === '/compare') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/compare#webpage`,
      name: 'Compare Rockhound Auto vs. Traditional Dealerships | Oregon Coast',
      description: 'See how Rockhound Auto compares to traditional Oregon Coast dealerships. Transparent pricing, no hidden fees, and honest service every time.',
      url: `${BASE_URL}/compare`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Compare', item: `${BASE_URL}/compare` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /why-choose-us ────────────────────────────────────────────────────────
  if (pathname === '/why-choose-us') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/why-choose-us#webpage`,
      name: 'Why Choose Rockhound Auto | Oregon Coast Used Car Dealer',
      description: 'Discover why Oregon Coast and SW Washington buyers choose Rockhound Auto. Transparent pricing, no-pressure sales, and honest service.',
      url: `${BASE_URL}/why-choose-us`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Why Choose Us', item: `${BASE_URL}/why-choose-us` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /how-it-works ─────────────────────────────────────────────────────────
  if (pathname === '/how-it-works') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/how-it-works#webpage`,
      name: 'How It Works | Buying a Car at Rockhound Auto Oregon',
      description: 'Learn how the Rockhound Auto buying process works — from browsing inventory to driving home. Simple, transparent, and stress-free.',
      url: `${BASE_URL}/how-it-works`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'How It Works', item: `${BASE_URL}/how-it-works` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-howto', {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      '@id': `${BASE_URL}/how-it-works#howto`,
      name: 'How to Buy a Used Car at Rockhound Auto — Two Ways',
      description: 'Two transparent, no-pressure paths to buying your next vehicle at Rockhound Auto in Warrenton, Oregon: buy online from our inventory, or use our concierge broker service to source any car nationwide.',
      image: {
        '@type': 'ImageObject',
        url: CORE_BUSINESS.logo,
      },
      totalTime: 'P7D',
      estimatedCost: {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: '995',
        description: 'Flat $995 broker fee when using the broker path. No fee for direct inventory purchases.',
      },
      supply: [
        {
          '@type': 'HowToSupply',
          name: 'Valid driver\'s license',
        },
        {
          '@type': 'HowToSupply',
          name: 'Proof of insurance',
        },
        {
          '@type': 'HowToSupply',
          name: 'Down payment (if financing)',
        },
      ],
      tool: [
        {
          '@type': 'HowToTool',
          name: 'Rockhound Auto online inventory',
        },
        {
          '@type': 'HowToTool',
          name: 'Online financing pre-approval form',
        },
      ],
      step: [
        // ── PATH A: Buy Online ──────────────────────────────────────────────
        {
          '@type': 'HowToSection',
          name: 'Path A: Buy Online from Our Inventory',
          description: 'Browse our curated inventory, get pre-approved, sign digitally, and have your vehicle delivered — no dealership visit required.',
          itemListElement: [
            {
              '@type': 'HowToStep',
              position: 1,
              name: 'Find Your Vehicle',
              text: 'Browse our curated inventory online at your own pace. Every listing includes real photos, full specs, transparent pricing, and a complete vehicle history report — no salesperson pressure.',
              url: `${BASE_URL}/inventory`,
              image: {
                '@type': 'ImageObject',
                url: `${BASE_URL}/og-image.jpg`,
              },
            },
            {
              '@type': 'HowToStep',
              position: 2,
              name: 'Get Financing Pre-Approved',
              text: 'Apply for financing online in minutes from home. We work with multiple lenders and welcome all credit situations — bad credit, no credit, and first-time buyers. Get a real rate, not a runaround.',
              url: `${BASE_URL}/financing`,
            },
            {
              '@type': 'HowToStep',
              position: 3,
              name: 'Sign Paperwork Digitally',
              text: 'Review and sign all documents on your own time — online or via FedEx overnight. No four-hour dealership marathon, no pressure to rush. You\'re done when you say you\'re done.',
              url: `${BASE_URL}/how-it-works`,
            },
            {
              '@type': 'HowToStep',
              position: 4,
              name: 'Receive Delivery to Your Door',
              text: 'Your vehicle is delivered to your home or office — often included in the purchase price. Free delivery within 50 miles of Warrenton, OR. No dealership visit ever required.',
              url: `${BASE_URL}/oregon-coast-delivery`,
            },
          ],
        },
        // ── PATH B: Use a Broker ────────────────────────────────────────────
        {
          '@type': 'HowToSection',
          name: 'Path B: Use Our Concierge Broker Service',
          description: 'Tell us exactly what you want. We search nationwide, inspect, negotiate, and deliver — for a flat $995 fee.',
          itemListElement: [
            {
              '@type': 'HowToStep',
              position: 1,
              name: 'Submit Your Vehicle Wish List',
              text: 'Tell us the make, model, year, features, and budget you\'re looking for. Not sure yet? We\'ll help you figure it out. No pressure, no pitch — just a conversation about what you actually need.',
              url: `${BASE_URL}/broker-services/request-a-vehicle`,
            },
            {
              '@type': 'HowToStep',
              position: 2,
              name: 'We Search Nationwide',
              text: 'While you live your life, we search dealer networks, auctions, and private sellers across all 50 states. No weekends lost, no unwanted test drives. We find the right match — not just the closest one.',
              url: `${BASE_URL}/broker-services`,
            },
            {
              '@type': 'HowToStep',
              position: 3,
              name: 'We Inspect and Negotiate',
              text: 'We inspect the vehicle, verify its full history, and negotiate hard on your behalf — with zero emotional attachment to the car. You approve every step. Nothing moves without your green light.',
              url: `${BASE_URL}/broker-services`,
            },
            {
              '@type': 'HowToStep',
              position: 4,
              name: 'Your Vehicle Arrives at Your Door',
              text: 'Paperwork handled. Transport arranged. Your vehicle arrives ready to drive. No dealership visit, no waiting room, no surprise fees. One flat $995 broker fee — that\'s it.',
              url: `${BASE_URL}/broker-services/request-a-vehicle`,
            },
          ],
        },
      ],
    });
    const howItWorksFaqs = pageFaqs.howItWorks;
    if (howItWorksFaqs && howItWorksFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(howItWorksFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /faq ──────────────────────────────────────────────────────────────────
  if (pathname === '/faq') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/faq#webpage`,
      name: 'Frequently Asked Questions | Rockhound Auto Oregon',
      description: 'Answers to common questions about buying used cars, financing, trade-ins, and broker services at Rockhound Auto in Warrenton, Oregon.',
      url: `${BASE_URL}/faq`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'FAQ', item: `${BASE_URL}/faq` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: ['h1', 'h2', '.faq-question', '.faq-answer'] },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /vlog ─────────────────────────────────────────────────────────────────
  if (pathname === '/vlog') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/vlog#webpage`,
      name: 'Vlog | Rockhound Auto Oregon Coast Car Dealer',
      description: 'Watch behind-the-scenes videos from Rockhound Auto. Vehicle walkthroughs, car buying tips, and Oregon Coast automotive content.',
      url: `${BASE_URL}/vlog`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Vlog', item: `${BASE_URL}/vlog` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    const videosFaqs = pageFaqs.videos;
    if (videosFaqs && videosFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(videosFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /videos ───────────────────────────────────────────────────────────────
  if (pathname === '/videos') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/videos#webpage`,
      name: 'Vehicle Videos | Rockhound Auto Oregon',
      description: 'Watch full video walkthroughs of every vehicle in our inventory. Rockhound Auto films every car so you can shop with confidence from anywhere.',
      url: `${BASE_URL}/videos`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Videos', item: `${BASE_URL}/videos` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    const videosFaqs = pageFaqs.videos;
    if (videosFaqs && videosFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(videosFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /customer-stories ─────────────────────────────────────────────────────
  if (pathname === '/customer-stories') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/customer-stories#webpage`,
      name: 'Customer Stories | Rockhound Auto Oregon Coast',
      description: 'Real stories from real customers who bought their vehicles through Rockhound Auto. See why Oregon Coast and SW Washington buyers trust us.',
      url: `${BASE_URL}/customer-stories`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Customer Stories', item: `${BASE_URL}/customer-stories` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    const customerStoriesFaqs = pageFaqs.customerStories;
    if (customerStoriesFaqs && customerStoriesFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(customerStoriesFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /deal-radar ───────────────────────────────────────────────────────────
  if (pathname === '/deal-radar') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/deal-radar#webpage`,
      name: 'Deal Radar — Best Used Car Deals Oregon Coast | Rockhound Auto',
      description: 'Get notified when the perfect used car deal hits our inventory. Set your criteria and Deal Radar alerts you instantly. Serving OR and WA.',
      url: `${BASE_URL}/deal-radar`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Deal Radar', item: `${BASE_URL}/deal-radar` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /sold-vehicles ────────────────────────────────────────────────────────
  if (pathname === '/sold-vehicles') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/sold-vehicles#webpage`,
      name: 'Recently Sold Vehicles | Rockhound Auto Oregon',
      description: 'Browse recently sold vehicles at Rockhound Auto. See what\'s moved and get a sense of our inventory quality and pricing on the Oregon Coast.',
      url: `${BASE_URL}/sold-vehicles`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Sold Vehicles', item: `${BASE_URL}/sold-vehicles` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    const soldVehiclesFaqs = pageFaqs.soldVehicles;
    if (soldVehiclesFaqs && soldVehiclesFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(soldVehiclesFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /cars-under-10k ───────────────────────────────────────────────────────
  if (pathname === '/cars-under-10k') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/cars-under-10k#webpage`,
      name: 'Used Cars Under $10,000 | Warrenton Oregon | Rockhound Auto',
      description: 'Quality used cars under $10,000 in Warrenton, Oregon. Inspected, reliable vehicles for every budget on the Oregon Coast and SW Washington.',
      url: `${BASE_URL}/cars-under-10k`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Cars Under $10k', item: `${BASE_URL}/cars-under-10k` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /cars-under-30k ───────────────────────────────────────────────────────
  if (pathname === '/cars-under-30k') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/cars-under-30k#webpage`,
      name: 'Used Cars Under $30,000 | Oregon Coast | Rockhound Auto',
      description: 'Browse quality used cars under $30,000 at Rockhound Auto in Warrenton, Oregon. SUVs, trucks, and sedans for every lifestyle on the Oregon Coast.',
      url: `${BASE_URL}/cars-under-30k`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Cars Under $30k', item: `${BASE_URL}/cars-under-30k` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /service-areas ────────────────────────────────────────────────────────
  if (pathname === '/service-areas') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/service-areas#webpage`,
      name: 'Service Areas | Rockhound Auto Oregon Coast & SW Washington',
      description: 'Rockhound Auto serves the entire Oregon Coast and SW Washington. Find your city and learn how we can help you buy or sell a vehicle near you.',
      url: `${BASE_URL}/service-areas`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Service Areas', item: `${BASE_URL}/service-areas` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-localbusiness', buildLocalBusiness({
      description: 'Rockhound Auto serves the Oregon Coast and SW Washington with quality used cars, auto broker services, and flexible financing.',
    }));
    return;
  }

  // ── /service-areas/:city pages ────────────────────────────────────────────
  const serviceAreaPages: Record<string, { city: string; state: string; stateCode: string }> = {
    '/service-areas/warrenton': { city: 'Warrenton', state: 'Oregon', stateCode: 'OR' },
    '/service-areas/astoria': { city: 'Astoria', state: 'Oregon', stateCode: 'OR' },
    '/service-areas/seaside': { city: 'Seaside', state: 'Oregon', stateCode: 'OR' },
    '/service-areas/portland': { city: 'Portland', state: 'Oregon', stateCode: 'OR' },
    '/service-areas/gearhart': { city: 'Gearhart', state: 'Oregon', stateCode: 'OR' },
    '/service-areas/medford': { city: 'Medford', state: 'Oregon', stateCode: 'OR' },
    '/service-areas/cannon-beach': { city: 'Cannon Beach', state: 'Oregon', stateCode: 'OR' },
    '/service-areas/tillamook': { city: 'Tillamook', state: 'Oregon', stateCode: 'OR' },
    '/service-areas/long-beach-wa': { city: 'Long Beach', state: 'Washington', stateCode: 'WA' },
    '/service-areas/clatsop-county': { city: 'Clatsop County', state: 'Oregon', stateCode: 'OR' },
  };
  if (serviceAreaPages[pathname]) {
    const { city, state, stateCode } = serviceAreaPages[pathname];
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}${pathname}#webpage`,
      name: `Used Cars ${city} ${stateCode} | Rockhound Auto`,
      description: `Quality used cars serving ${city}, ${state}. Rockhound Auto offers transparent pricing, no hidden fees, and flexible financing for all credit situations.`,
      url: `${BASE_URL}${pathname}`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Service Areas', item: `${BASE_URL}/service-areas` },
        { name: city, item: `${BASE_URL}${pathname}` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-localbusiness', buildLocalBusiness({
      description: `Rockhound Auto serves ${city}, ${state} with quality used cars, auto broker services, and flexible financing. Located just 10 minutes from Astoria in Warrenton.`,
      areaServed: [{ '@type': 'City', name: city, containedInPlace: { '@type': 'State', name: state } }],
    }));
    // Add FAQ for SW Washington specifically
    if (pathname === '/service-areas/sw-washington') {
      const swWashingtonFaqs = pageFaqs.usedCarsSWWashington;
      if (swWashingtonFaqs && swWashingtonFaqs.length > 0) {
        injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(swWashingtonFaqs, DEFAULT_AUTHOR));
      }
    }
    return;
  }

  // ── /vs/:competitor pages ─────────────────────────────────────────────────
  const competitorPages: Record<string, { competitor: string }> = {
    '/vs/astoria-ford': { competitor: 'Astoria Ford' },
    '/vs/lums-toyota': { competitor: "Lum's Toyota" },
    '/vs/oceancrest-chevrolet': { competitor: 'Oceancrest Chevrolet' },
    '/vs/warrenton-kia': { competitor: 'Warrenton Kia' },
  };
  if (competitorPages[pathname]) {
    const { competitor } = competitorPages[pathname];
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}${pathname}#webpage`,
      name: `Rockhound Auto vs. ${competitor} | Oregon Coast Car Dealer Comparison`,
      description: `Compare Rockhound Auto to ${competitor}. See why Oregon Coast buyers choose Rockhound Auto for transparent pricing, no hidden fees, and honest service.`,
      url: `${BASE_URL}${pathname}`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Compare', item: `${BASE_URL}/compare` },
        { name: `vs. ${competitor}`, item: `${BASE_URL}${pathname}` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /used-cars-astoria-oregon ─────────────────────────────────────────────
  if (pathname === '/used-cars-astoria-oregon') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/used-cars-astoria-oregon#webpage`,
      name: 'Used Cars Astoria Oregon | Rockhound Auto Warrenton OR',
      description: 'Quality used cars near Astoria, Oregon. Rockhound Auto in Warrenton is just 10 minutes from Astoria — transparent pricing, no hidden fees, all credit welcome.',
      url: `${BASE_URL}/used-cars-astoria-oregon`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Used Cars Astoria Oregon', item: `${BASE_URL}/used-cars-astoria-oregon` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-localbusiness', buildLocalBusiness({
      description: 'Rockhound Auto serves Astoria, Oregon with quality used cars, auto broker services, and flexible financing. Located just 10 minutes from Astoria in Warrenton.',
      areaServed: [{ '@type': 'City', name: 'Astoria', containedInPlace: { '@type': 'State', name: 'Oregon' } }],
    }));
    const astoriaFaqs = pageFaqs.usedCarsAstoriaOregon;
    if (astoriaFaqs && astoriaFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(astoriaFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── Local landing pages ───────────────────────────────────────────────────
  const localLandingPages: Record<string, { city: string; state: string; stateCode: string }> = {
    '/used-cars-seaside-oregon': { city: 'Seaside', state: 'Oregon', stateCode: 'OR' },
    '/used-cars-tillamook-oregon': { city: 'Tillamook', state: 'Oregon', stateCode: 'OR' },
    '/used-cars-portland-oregon': { city: 'Portland', state: 'Oregon', stateCode: 'OR' },
    '/used-cars-sw-washington': { city: 'SW Washington', state: 'Washington', stateCode: 'WA' },
    '/used-cars-ilwaco-washington': { city: 'Ilwaco', state: 'Washington', stateCode: 'WA' },
    '/used-cars-longview-washington': { city: 'Longview', state: 'Washington', stateCode: 'WA' },
  };
  if (localLandingPages[pathname]) {
    const { city, state, stateCode } = localLandingPages[pathname];
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}${pathname}#webpage`,
      name: `Used Cars ${city} ${stateCode} | Rockhound Auto`,
      description: `Quality used cars serving ${city}, ${state}. Rockhound Auto offers transparent pricing, no hidden fees, and flexible financing for all credit situations.`,
      url: `${BASE_URL}${pathname}`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: `Used Cars ${city} ${stateCode}`, item: `${BASE_URL}${pathname}` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-localbusiness', buildLocalBusiness({
      description: `Rockhound Auto serves ${city}, ${state} with quality used cars, auto broker services, and flexible financing for all credit situations.`,
      areaServed: [{ '@type': 'City', name: city, containedInPlace: { '@type': 'State', name: state } }],
    }));
    // Add FAQ for SW Washington specifically
    if (pathname === '/used-cars-sw-washington') {
      const swWashingtonFaqs = pageFaqs.usedCarsSWWashington;
      if (swWashingtonFaqs && swWashingtonFaqs.length > 0) {
        injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(swWashingtonFaqs, DEFAULT_AUTHOR));
      }
    }
    return;
  }

  // ── /used-suvs-warrenton-oregon ───────────────────────────────────────────
  if (pathname === '/used-suvs-warrenton-oregon') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/used-suvs-warrenton-oregon#webpage`,
      name: 'Used SUVs Warrenton Oregon | Rockhound Auto',
      description: 'Quality used SUVs in Warrenton, Oregon. AWD and 4WD options perfect for Oregon Coast weather. Transparent pricing, all credit welcome.',
      url: `${BASE_URL}/used-suvs-warrenton-oregon`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Used SUVs Warrenton Oregon', item: `${BASE_URL}/used-suvs-warrenton-oregon` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    const suvsFaqs = pageFaqs.usedSuvsWarrentonOregon;
    if (suvsFaqs && suvsFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(suvsFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /used-trucks-warrenton-oregon ─────────────────────────────────────────
  if (pathname === '/used-trucks-warrenton-oregon') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/used-trucks-warrenton-oregon#webpage`,
      name: 'Used Trucks Warrenton Oregon | Rockhound Auto',
      description: 'Quality used trucks in Warrenton, Oregon. Work trucks, off-road rigs, and daily drivers. Transparent pricing, all credit welcome.',
      url: `${BASE_URL}/used-trucks-warrenton-oregon`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Used Trucks Warrenton Oregon', item: `${BASE_URL}/used-trucks-warrenton-oregon` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    const trucksFaqs = pageFaqs.usedTrucksWarrentonOregon;
    if (trucksFaqs && trucksFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(trucksFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── /blog ─────────────────────────────────────────────────────────────────
  if (pathname === '/blog') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}/blog#webpage`,
      name: 'Car Buying Blog | Rockhound Auto Oregon Coast',
      description: 'Expert car buying tips, Oregon auto market insights, and local guides from Rockhound Auto in Warrenton, Oregon.',
      url: `${BASE_URL}/blog`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Blog', item: `${BASE_URL}/blog` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /blog/:slug ───────────────────────────────────────────────────────────
  if (pathname.startsWith('/blog/') && pathname !== '/blog/newsletter') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}${pathname}#webpage`,
      name: 'Blog Article | Rockhound Auto Oregon',
      description: 'Car buying tips and Oregon automotive insights from Rockhound Auto.',
      url: `${BASE_URL}${pathname}`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: 'Blog', item: `${BASE_URL}/blog` },
        { name: 'Article', item: `${BASE_URL}${pathname}` },
      ]),
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    return;
  }

  // ── /contact ──────────────────────────────────────────────────────────────
  if (pathname === '/contact') {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${BASE_URL}/contact#webpage`,
      name: 'Contact Rockhound Auto | Warrenton, OR Used Car Dealer',
      description: 'Contact Rockhound Auto in Warrenton, Oregon. Call, text, email, or visit us. We serve Astoria, Seaside, Cannon Beach, and the entire Oregon Coast.',
      url: `${BASE_URL}/contact`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      about: { '@id': SCHEMA_IDS.automotiveBusiness },
      breadcrumb: { '@id': `${BASE_URL}/contact#breadcrumb` },
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });
    injectJsonLd('schema-manager-localbusiness', {
      '@context': 'https://schema.org',
      '@type': ['AutoDealer', 'LocalBusiness'],
      '@id': SCHEMA_IDS.autoDealer,
      name: CORE_BUSINESS.name,
      legalName: CORE_BUSINESS.legalName,
      url: CORE_BUSINESS.url,
      telephone: CORE_BUSINESS.telephone,
      email: CORE_BUSINESS.email,
      description: 'Contact Rockhound Auto in Warrenton, Oregon for used car sales, auto broker services, financing, and trade-in appraisals. Serving the Oregon Coast and SW Washington.',
      logo: {
        '@type': 'ImageObject',
        url: CORE_BUSINESS.logo,
        width: 400,
        height: 120,
      },
      image: CORE_BUSINESS.logo,
      address: {
        '@type': 'PostalAddress',
        ...CORE_BUSINESS.address,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: CORE_BUSINESS.geo.latitude,
        longitude: CORE_BUSINESS.geo.longitude,
      },
      hasMap: CORE_BUSINESS.mapsUrl,
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
          ],
          opens: '09:00',
          closes: '18:00',
          description: 'By Appointment — call or text to schedule',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday'],
          opens: '10:00',
          closes: '16:00',
          description: 'By Appointment — call or text to schedule',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Sunday'],
          opens: '00:00',
          closes: '00:00',
          description: 'Closed — contact us to arrange a special appointment',
        },
      ],
      priceRange: CORE_BUSINESS.priceRange,
      currenciesAccepted: CORE_BUSINESS.currenciesAccepted,
      paymentAccepted: CORE_BUSINESS.paymentAccepted.join(', '),
      areaServed: [
        { '@type': 'State', name: 'Oregon' },
        { '@type': 'State', name: 'Washington' },
      ],
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: CORE_BUSINESS.telephone,
          contactType: 'sales',
          areaServed: ['OR', 'WA'],
          availableLanguage: 'English',
          contactOption: 'TollFree',
        },
        {
          '@type': 'ContactPoint',
          email: CORE_BUSINESS.email,
          contactType: 'customer support',
          areaServed: ['OR', 'WA'],
          availableLanguage: 'English',
        },
      ],
      sameAs: CORE_BUSINESS.sameAs,
      foundingDate: CORE_BUSINESS.foundingDate,
      slogan: CORE_BUSINESS.slogan,
    });
    injectJsonLd('schema-manager-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${BASE_URL}/contact#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Contact', item: `${BASE_URL}/contact` },
      ],
    });
    const contactFaqs = pageFaqs.contact;
    if (contactFaqs && contactFaqs.length > 0) {
      injectJsonLd('schema-manager-page-faq', buildEnhancedFAQSchema(contactFaqs, DEFAULT_AUTHOR));
    }
    return;
  }

  // ── Lazy content pages — WebPage + speakable + FAQ ────────────────────────
  const LAZY_CONTENT_PAGES: Record<string, { name: string; description: string; faqKey?: keyof typeof pageFaqs }> = {
    '/best-trucks-under-30k-oregon': {
      name: 'Best Trucks Under $30k in Oregon | Rockhound Auto',
      description: 'Find the best used trucks under $30,000 in Oregon. Expert picks for Toyota Tacoma, Ford F-150, and more.',
      faqKey: 'bestTrucksUnder30kOregon',
    },
    '/awd-vehicles-oregon-coast': {
      name: 'Best AWD Vehicles for Oregon Coast | Rockhound Auto',
      description: 'Top AWD vehicles for Oregon Coast weather and terrain. Expert recommendations for all-wheel drive cars and SUVs.',
      faqKey: 'awdVehiclesOregonCoast',
    },
    '/best-awd-cars-oregon-washington': {
      name: 'Best AWD Cars for Oregon & Washington | Rockhound Auto',
      description: 'Top AWD cars for Pacific Northwest weather. Expert picks for Oregon and Washington drivers.',
      faqKey: 'bestAwdCarsOregonWashington',
    },
    '/best-awd-cars-under-25k': {
      name: 'Best AWD Cars Under $25k | Rockhound Auto',
      description: 'Find the best AWD vehicles under $25,000. Reliable all-wheel drive cars for every budget.',
      faqKey: 'bestAwdCarsUnder25k',
    },
    '/best-cars-commuting-portland': {
      name: 'Best Cars for Commuting to Portland | Rockhound Auto',
      description: 'Top cars for the Portland commute from the Oregon coast. Fuel-efficient and reliable options.',
      faqKey: 'bestCarsCommutingPortland',
    },
    '/best-cars-oregon-coast-weather': {
      name: 'Best Cars for Oregon Coast Weather | Rockhound Auto',
      description: 'Top vehicles for Oregon coast rain, fog, and coastal conditions. Expert recommendations.',
      faqKey: 'bestCarsOregonCoastWeather',
    },
    '/best-family-cars-oregon': {
      name: 'Best Family Cars in Oregon | Rockhound Auto',
      description: 'Top family vehicles for Oregon drivers. Safe, spacious, and reliable options for every family.',
      faqKey: 'familySuvsOregon',
    },
    '/best-first-time-buyer-cars': {
      name: 'Best Cars for First-Time Buyers | Rockhound Auto',
      description: 'Top vehicles for first-time car buyers. Affordable, reliable, and easy to finance.',
      faqKey: 'carsUnder10k',
    },
    '/best-used-cars-under-15k': {
      name: 'Best Used Cars Under $15k | Rockhound Auto',
      description: 'Find the best used cars under $15,000. Reliable transportation on a budget.',
      faqKey: 'carsUnder10k',
    },
    '/best-used-suvs-coastal-weather': {
      name: 'Best Used SUVs for Coastal Weather | Rockhound Auto',
      description: 'Top used SUVs for Oregon and Washington coastal weather. AWD and reliable options.',
      faqKey: 'bestUsedSuvsCoastalWeather',
    },
    '/best-used-suvs-under-20k': {
      name: 'Best Used SUVs Under $20k | Rockhound Auto',
      description: 'Find the best used SUVs under $20,000. Reliable all-wheel drive options for every budget.',
      faqKey: 'bestUsedSuvsCoastalWeather',
    },
    '/family-suvs-oregon': {
      name: 'Family SUVs in Oregon | Rockhound Auto',
      description: 'Best family SUVs for Oregon drivers. Spacious, safe, and AWD-capable options.',
      faqKey: 'familySuvsOregon',
    },
    '/toyota-rav4-vs-honda-crv-oregon': {
      name: 'Toyota RAV4 vs Honda CR-V in Oregon | Rockhound Auto',
      description: 'Detailed comparison of Toyota RAV4 and Honda CR-V for Oregon drivers.',
      faqKey: 'toyotaRav4VsHondaCrvOregon',
    },
    '/used-cars-under-20000': {
      name: 'Used Cars Under $20,000 | Rockhound Auto',
      description: 'Quality used cars under $20,000 in Oregon. Reliable vehicles for every budget.',
      faqKey: 'usedCarsUnder20000',
    },
  };

  const pageConfig = LAZY_CONTENT_PAGES[pathname];
  if (pageConfig) {
    injectJsonLd('schema-manager-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE_URL}${pathname}#webpage`,
      name: pageConfig.name,
      description: pageConfig.description,
      url: `${BASE_URL}${pathname}`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: buildBreadcrumbSchema([
        { name: 'Home', item: `${BASE_URL}/` },
        { name: pageConfig.name.split('|')[0].trim(), item: `${BASE_URL}${pathname}` },
      ]),
      speakable: { '@type': 'SpeakableSpecification', cssSelector: DEFAULT_SPEAKABLE_SELECTORS },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    });

    injectJsonLd('schema-manager-speakable', buildSpeakableSchema(pathname, DEFAULT_SPEAKABLE_SELECTORS));

    if (pageConfig.faqKey) {
      const faqs = pageFaqs[pageConfig.faqKey];
      if (faqs && faqs.length > 0) {
        injectJsonLd('schema-manager-faq', buildEnhancedFAQSchema(faqs, DEFAULT_AUTHOR));
      }
    }
  }
}

export default function SchemaManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    injectForPath(pathname);
    return cleanup;
  }, [pathname]);

  return null;
}
